import React, { useState, useEffect, useMemo } from "react";
import {
  ArrowLeft,
  User,
  Briefcase,
  CreditCard,
  IndianRupee,
  Calendar,
  MapPin,
  Mail,
  Phone,
  Smartphone,
  Banknote,
  List,
  CheckCircle,
  XCircle,
  Clock,
  Star,
  Loader2,
  Info,
  Send,
  RefreshCw,
  AlertTriangle,
  Droplet,
  ShieldCheck,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { getEmployeeById, sendEmployeeInvite } from "@/services/adminService";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useLocation, useNavigate } from "react-router-dom";
import EmployeeDocuments from "../employee/EmployeeDocuments";
import EmployeeLeave from "../employee/EmployeeLeave";
import EmployeeLoans from "../employee/EmployeeLoans";
import EmployeePayslips from "../employee/EmployeePayslips";
import { useSubscription } from "@/contexts/SubscriptionContext";
import EmployeeAttendanceNew from "../employee/EmployeeAttendance";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import CareerHistory from "../CareerHistory";
import { formatEnumLabel } from "@/lib/utils";
import { usePermission } from "@/contexts/RoleAccessContext";
import { createCanHelpers } from "@/utils/rbac";
import { getErrorMessage, isAuthExpiryError } from "@/utils/errorHandler";
import RatingMeter from "@/components/ui/RatingMeter";
import useRatingConfig from "@/hooks/useRatingConfig";

function formatDate(dateString) {
  if (!dateString) return "N/A";
  try {
    const options = { year: "numeric", month: "short", day: "numeric" };
    return new Date(dateString).toLocaleDateString("en-US", options);
  } catch (e) {
    return dateString;
  }
}

const ReadOnlyField = ({ icon: Icon, label, value, className = "" }) => (
  <div className={`space-y-1 ${className}`}>
    <div className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400 flex items-center gap-2">
      {Icon && <Icon className="w-3 h-3 text-primary" />}
      {label}
    </div>
    <div className="text-base font-medium text-gray-900 dark:text-white break-words">
      {value || <span className="text-gray-400 italic">Not specified</span>}
    </div>
  </div>
);

const InfoPill = ({ status }) => {
  let style = "bg-gray-100 text-gray-600";
  let text = "N/A";

  switch (status.toLowerCase()) {
    case "active":
      style =
        "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400";
      text = "Active";
      break;
    case "terminated":
      style = "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400";
      text = "Terminated";
      break;
    case "on_leave":
      style =
        "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400";
      text = "On Leave";
      break;
    default:
      style = "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300";
      text = status;
  }

  return (
    <span
      className={`inline-flex items-center px-3 py-1 text-sm font-semibold rounded-full ${style}`}
    >
      {text}
    </span>
  );
};

const ViewEmployeeDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id, empId } = location.state || {};
  const { hasFeature } = useSubscription();

  const employeeId = id;
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteMode, setInviteMode] = useState(null); // "send" | "resend"
  const showDocumentTab = hasFeature("document_upload");
  const showDepartmentDesignation = hasFeature("department_mapping");

  const { can } = usePermission();
  // Bands are anchored to base_rating, so this screen needs the configured
  // scale — it previously had neither base nor max and rendered a bare number.
  const { scale: ratingScale } = useRatingConfig();
  const { canEdit } = createCanHelpers(can);
  const canEditEmployee = canEdit("employees");

  const fetchEmployeeData = async () => {
    try {
      const res = await getEmployeeById(employeeId);
      return res;
    } catch (error) {
      if (isAuthExpiryError(error)) throw error;
      throw new Error(getErrorMessage(error, "Failed to fetch employee details"));
    }
  };

  const loadEmployee = async () => {
    try {
      setLoading(true);
      const emp = await fetchEmployeeData();
      setEmployee(emp?.data);
    } catch (error) {
      console.error("Failed to load employee data:", error);
      setEmployee(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEmployee();
  }, []);

  const { earnings, deductions, grossSalary, netSalary } = useMemo(() => {
    if (!employee || !employee.salary_structure) {
      return { earnings: [], deductions: [], grossSalary: 0, netSalary: 0 };
    }

    const allComponents = employee.salary_structure;

    const earningsList = allComponents.filter((c) => c.category === "earnings");
    const deductionsList = allComponents.filter(
      (c) => c.category === "deductions"
    );

    const gross = earningsList.reduce((sum, c) => sum + (c.amount || 0), 0);
    const totalDeductions = deductionsList.reduce(
      (sum, c) => sum + (c.amount || 0),
      0
    );
    const net = gross - totalDeductions;

    return {
      earnings: earningsList,
      deductions: deductionsList,
      grossSalary: gross,
      netSalary: net,
    };
  }, [employee]);

  const openInviteDialog = (id, mode) => {
    setInviteMode(mode);
    setInviteDialogOpen(true);
  };

  const handleSendInvite = async () => {
    if (!empId) return;

    try {
      setInviteLoading(true);

      await sendEmployeeInvite(empId);

      toast.success(
        inviteMode === "send"
          ? "Invitation email sent successfully."
          : "Invitation email re-sent successfully."
      );
      loadEmployee();
      setInviteDialogOpen(false);
    } catch (err) {
      toast.error("Failed to send invite. Please try again.");
    } finally {
      setInviteLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6 p-6">
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <Skeleton className="h-9 w-64" />
            <Skeleton className="h-4 w-80" />
          </div>
          <Skeleton className="h-10 w-32" />
        </div>
        <Card>
          <CardContent className="p-6 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {Array.from({ length: 9 }).map((_, i) => (
                <div key={i} className="space-y-2">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-10 w-full" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
        <Skeleton className="h-12 w-full" />
        <Card>
          <CardContent className="p-6 space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-14 w-full" />
            ))}
          </CardContent>
        </Card>
        <div className="flex justify-end gap-3">
          <Skeleton className="h-10 w-24" />
          <Skeleton className="h-10 w-32" />
        </div>
      </div>
    );
  }

  if (!employee) {
    return (
      <div className="space-y-6 p-8 bg-gray-50 dark:bg-gray-900 min-h-screen">
        <div className="text-center p-10 bg-white dark:bg-gray-800 rounded-xl shadow-lg">
          <h1 className="text-3xl font-bold text-red-600">
            Employee Not Found
          </h1>
          <p className="text-gray-500 mt-2">
            The employee with ID "{id}" could not be loaded.
          </p>
          <Button onClick={() => navigate("/employees")} className="mt-6">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Employee List
          </Button>
        </div>
      </div>
    );
  }

  const DataSection = ({ title, icon: Icon, children }) => (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center space-x-3 text-primary dark:text-primary">
          <Icon className="h-6 w-6" />
          <span>{title}</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {children}
      </CardContent>
    </Card>
  );

  const getStatusIcon = (status) => {
    switch (status.toLowerCase()) {
      case "active":
        return <CheckCircle className="w-5 h-5 text-emerald-500" />;
      case "terminated":
        return <XCircle className="w-5 h-5 text-red-500" />;
      default:
        return <Clock className="w-5 h-5 text-amber-500" />;
    }
  };

  return (
    <div>
      <div className="flex items-start gap-3 sm:gap-4 pb-6 sm:pb-8">
        {/* Back */}
        <Button
          variant="ghost"
          size="icon"
          className="h-10 w-10 shrink-0 rounded-full border border-border bg-background hover:bg-accent hover:text-accent-foreground transition"
          onClick={() => navigate("/employees")}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>

        {/* Avatar */}
        <div className="h-14 w-14 sm:h-16 sm:w-16 shrink-0 rounded-full border-2 border-primary/70 bg-primary/10 overflow-hidden flex items-center justify-center">
          {employee.profilePhoto ? (
            <img
              src={employee.profilePhoto}
              alt={employee.name}
              className="h-full w-full object-cover"
            />
          ) : (
            <span className="text-lg sm:text-xl font-semibold text-primary">
              {employee.name?.[0]?.toUpperCase()}
            </span>
          )}
        </div>

        {/* Title + meta */}
        <div className="min-w-0 flex-1">
          {/* Name + rating on same row */}
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h1 className="truncate text-xl sm:text-2xl font-bold text-foreground">
                {employee.name}
              </h1>

              {/* Designation + status row */}
              <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                {showDepartmentDesignation && (
                  <span className="truncate max-w-[18rem]">
                    {employee.designation}
                  </span>
                )}
                <InfoPill status={formatEnumLabel(employee.status)} />
                <span className="inline-flex items-center">
                  {getStatusIcon(employee.status)}
                  {employee.status === "exit_scheduled" && (
                    <span className="ml-1 text-xs text-amber-500">
                      (Exit on {formatDate(employee.last_working_date)})
                    </span>
                  )}
                </span>
              </div>
            </div>

            {/* Rating block aligned right.
                Previously this rendered a raw, unclamped value with no maximum
                beside a one-element star array that was always yellow — so it
                neither showed the scale nor reflected performance. It now uses
                the shared meter, so the value, the band and the colour match
                every other screen. */}
            {Number.isFinite(Number(employee.rating)) && (
              <div className="w-40 shrink-0">
                <RatingMeter
                  rating={employee.rating}
                  scale={ratingScale}
                  size="sm"
                  digits={2}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Pending reimbursement claims alert — must be cleared before exit */}
      {employee?.has_pending_reimbursements && (
        <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg dark:bg-amber-900/20 dark:border-amber-800">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-semibold text-amber-800 dark:text-amber-300">
                {employee.pending_reimbursement_summary?.length || 0} pending
                reimbursement claim(s) awaiting review
              </p>
              <p className="text-sm text-amber-700 dark:text-amber-400">
                Approve or reject these in the Reimbursements section. The exit
                process cannot be initiated until all are cleared; approved
                amounts are paid with payroll.
              </p>
              {employee.pending_reimbursement_summary?.length > 0 && (
                <ul className="mt-2 text-xs text-amber-700 dark:text-amber-400 list-disc list-inside space-y-0.5">
                  {employee.pending_reimbursement_summary.map((c) => (
                    <li key={c.claim_id}>
                      {c.claim_type} — ₹{Number(c.amount || 0).toLocaleString()}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}

      <Tabs defaultValue="overview">
        <TabsList className="flex justify-start space-x-8 overflow-x-auto bg-transparent h-auto p-0 no-scrollbar">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          {showDocumentTab && (
            <TabsTrigger value="documents">Documents</TabsTrigger>
          )}
          <TabsTrigger value="payslip">Payslips</TabsTrigger>
          <TabsTrigger value="leave">Leaves</TabsTrigger>
          <TabsTrigger value="attendance">Attendance</TabsTrigger>
          <TabsTrigger value="Finances">Finances</TabsTrigger>
          <TabsTrigger value="career">Career History</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <div className=" space-y-8 bg-gray-50 bg-transparent min-h-screen">
            {/* Header */}
            <div className="flex justify-end">
              {/* EMPLOYEE PORTAL INVITE STATUS */}
              {hasFeature("employee_portal") && (
                <div className="flex justify-end mt-2">
                  {!employee?.company_settings
                    ?.employee_portal_enabled ? null : (
                    <div className="flex items-center gap-3">
                      {/* ACCESS ENABLED */}
                      {employee?.company_settings?.employee_portal_enabled && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Badge
                                className={`flex items-center gap-1.5 mr-3 h-9 px-4 py-2 text-sm font-semibold ${
                                  ["terminated", "resigned"].includes(
                                    employee.status,
                                  )
                                    ? "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300"
                                    : employee.is_invitation_accepted
                                      ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300"
                                      : "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300"
                                }`}
                              >
                                {["terminated", "resigned"].includes(
                                  employee.status,
                                )
                                  ? "Access Disabled"
                                  : employee.is_invitation_accepted
                                    ? "Access Enabled"
                                    : "Invite Pending"}
                                <Info className="h-4 w-4 opacity-80" />
                              </Badge>
                            </TooltipTrigger>

                            <TooltipContent className="max-w-sm text-xs leading-relaxed">
                              {["terminated", "resigned"].includes(
                                employee.status,
                              ) ? (
                                <>
                                  Portal access is permanently disabled because
                                  the employee is no longer active. Terminated
                                  or resigned employees cannot log in.
                                </>
                              ) : employee.is_invitation_accepted ? (
                                <>
                                  This employee has activated their portal
                                  account and can log in. Access remains enabled
                                  while the employee status is active.
                                </>
                              ) : (
                                <>
                                  An invitation has been sent, but the employee
                                  has not yet activated their portal account.
                                </>
                              )}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}

                      {/* SEND INVITE */}
                      {!employee.is_invitation_sent &&
                        !employee.is_invitation_accepted && (
                          <Button
                            variant="default"
                            className="flex items-center gap-2 mr-3"
                            onClick={() => openInviteDialog("send")}
                          >
                            <Send className="h-4 w-4" />
                            Send Invite
                          </Button>
                        )}

                      {/* RE-INVITE */}
                      {employee.is_invitation_sent &&
                        !employee.is_invitation_accepted && (
                          <Button
                            variant="outline"
                            className="
                            flex items-center gap-2 mr-3
                          "
                            onClick={() => openInviteDialog("resend")}
                          >
                            <RefreshCw className="h-4 w-4" />
                            Re-Invite
                          </Button>
                        )}
                    </div>
                  )}
                </div>
              )}

              <div className="flex items-end justify-end">
                <Button
                  className={`${
                    !canEditEmployee
                      ? " w-full sm:w-auto opacity-70 cursor-not-allowed hover:bg-primary"
                      : "w-full sm:w-auto "
                  }`}
                  onClick={() => {
                    if (!canEdit("employees", true)) return;
                    navigate(`/employees/${employee.employee_id}/edit`);
                  }}
                >
                  <User className="w-4 h-4 mr-2" />
                  Edit Employee
                </Button>
              </div>
            </div>

            <div className="grid gap-8 lg:grid-cols-3">
              {/* Contact and Job Info (Col 1 & 2) */}
              <div className="lg:col-span-2 space-y-8">
                {/* Personal Information */}
                <DataSection title="Personal Information" icon={User}>
                  <ReadOnlyField
                    icon={User}
                    label="Full Name"
                    value={employee.name}
                  />
                  <ReadOnlyField
                    icon={Mail}
                    label="Email Address"
                    value={employee.email}
                  />
                  <ReadOnlyField
                    icon={Phone}
                    label="Phone Number"
                    value={employee.phone}
                  />
                  <ReadOnlyField
                    icon={Calendar}
                    label="Date of Birth"
                    value={formatDate(employee.date_of_birth)}
                  />
                  <ReadOnlyField
                    icon={List}
                    label="Gender"
                    value={
                      employee.gender?.charAt(0).toUpperCase() +
                      employee.gender?.slice(1)
                    }
                  />
                  <ReadOnlyField
                    icon={List}
                    label="Marital Status"
                    value={
                      employee.marital_status?.charAt(0).toUpperCase() +
                      employee.marital_status?.slice(1)
                    }
                  />
                  <ReadOnlyField
                    icon={CreditCard}
                    label="Aadhar Number"
                    value={employee.aadhar_number}
                  />
                  <ReadOnlyField
                    icon={CreditCard}
                    label="PAN Number"
                    value={employee.pan_number}
                  />
                  <ReadOnlyField
                    icon={MapPin}
                    label="Address"
                    value={employee.address}
                    className="col-span-1 sm:col-span-2 lg:col-span-3"
                  />

                  {/* Emergency Contact & Medical Information */}
                  <div className="col-span-1 sm:col-span-2 lg:col-span-3 space-y-4">
                    <Separator />
                    <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                      Emergency Contact & Medical Information
                    </h3>
                  </div>
                  <ReadOnlyField
                    icon={User}
                    label="Emergency Contact Name"
                    value={employee.emergency_contact_name}
                  />
                  <ReadOnlyField
                    icon={Phone}
                    label="Emergency Contact Phone"
                    value={employee.emergency_contact_phone}
                  />
                  <ReadOnlyField
                    icon={Droplet}
                    label="Blood Group"
                    value={employee.blood_group}
                  />
                </DataSection>

                {/* Job Information */}
                <DataSection title="Job Information" icon={Briefcase}>
                  {showDepartmentDesignation && (
                    <>
                      <ReadOnlyField
                        icon={Briefcase}
                        label="Department"
                        value={employee.department}
                      />
                      <ReadOnlyField
                        icon={Briefcase}
                        label="Designation"
                        value={employee.designation}
                      />
                    </>
                  )}
                  <ReadOnlyField
                    icon={Calendar}
                    label="Date of Joining"
                    value={formatDate(employee.date_of_joining)}
                  />
                  <ReadOnlyField
                    icon={MapPin}
                    label="Work Location"
                    value={employee.work_location}
                  />
                  <ReadOnlyField
                    icon={Clock}
                    label="Probation Status"
                    value={
                      employee.is_on_probation
                        ? `On Probation (Ends: ${formatDate(
                            employee.probation_end_date,
                          )})`
                        : "Permanent"
                    }
                  />
                  {["terminated", "resigned", "exit_scheduled"].includes(
                    employee.status,
                  ) && (
                    <ReadOnlyField
                      icon={Calendar}
                      label="Last Working Date"
                      value={formatDate(employee.last_working_date)}
                    />
                  )}
                  {/* Admin access is managed in Users & Roles; shown here for reference. */}
                  {hasFeature("roles_and_access") && employee.admin_access && (
                    <ReadOnlyField
                      icon={ShieldCheck}
                      label="Admin Access"
                      value={
                        <span className="flex flex-wrap items-baseline gap-x-2">
                          <span>
                            {employee.admin_access.is_admin
                              ? employee.admin_access.role_name || "Admin"
                              : "None"}
                          </span>
                          {can("roles_and_access", "view") && (
                            <button
                              type="button"
                              className="text-sm font-normal text-primary hover:underline"
                              onClick={() => navigate("/permission")}
                            >
                              Manage in Users & Roles
                            </button>
                          )}
                        </span>
                      }
                    />
                  )}
                </DataSection>

                {/* Bank Information */}
                <DataSection title="Bank Information" icon={Banknote}>
                  <ReadOnlyField
                    icon={Banknote}
                    label="Bank Name"
                    value={employee.bank_info?.bank_name}
                  />
                  <ReadOnlyField
                    icon={Briefcase}
                    label="Branch"
                    value={employee.bank_info?.branch}
                  />
                  <ReadOnlyField
                    icon={List}
                    label="Account Number"
                    value={employee.bank_info?.account_number}
                  />
                  <ReadOnlyField
                    icon={Smartphone}
                    label="IFSC Code"
                    value={employee.bank_info?.ifsc_code}
                  />
                </DataSection>
              </div>

              {/* Salary Structure & Summary (Col 3) */}
              <div className="lg:col-span-1">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center space-x-3 text-primary dark:text-primary">
                      <IndianRupee className="h-6 w-6" />
                      <span>Monthly Salary Breakdown</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Earnings */}
                    <div>
                      <h3 className="text-lg font-bold text-emerald-600 mb-3 border-b pb-1 dark:border-gray-700">
                        Earnings
                      </h3>
                      <div className="space-y-2">
                        {earnings.length > 0 ? (
                          earnings.map((c) => (
                            <div
                              key={c.component_id}
                              className="flex justify-between text-sm text-gray-700 dark:text-gray-300"
                            >
                              <span className="truncate pr-4">
                                {c.name_in_payslip}
                              </span>
                              <span className="font-semibold whitespace-nowrap">
                                ₹{c.amount.toLocaleString("en-IN")}
                              </span>
                            </div>
                          ))
                        ) : (
                          <p className="text-sm text-gray-500 italic">
                            No earnings defined.
                          </p>
                        )}
                      </div>
                    </div>

                    <Separator />

                    {/* Deductions */}
                    <div>
                      <h3 className="text-lg font-bold  text-red-600 dark:text-red-500 mb-3 border-b pb-1 dark:border-gray-700">
                        Deductions
                      </h3>
                      <div className="space-y-2">
                        {deductions.length > 0 ? (
                          deductions.map((c) => (
                            <div
                              key={c.component_id}
                              className="flex justify-between text-sm text-gray-700 dark:text-gray-300"
                            >
                              <span className="truncate pr-4">
                                {c.name_in_payslip}
                              </span>
                              <span className="font-semibold whitespace-nowrap text-red-500">
                                -₹{c.amount.toLocaleString("en-IN")}
                              </span>
                            </div>
                          ))
                        ) : (
                          <p className="text-sm text-gray-500 italic">
                            No deductions defined.
                          </p>
                        )}
                      </div>
                    </div>

                    <Separator />

                    {/* Summary */}
                    <div className="pt-2 space-y-3">
                      <div className="flex justify-between items-center text-base font-semibold text-gray-900 dark:text-white">
                        <span>Gross Monthly Salary</span>
                        <span className="text-xl text-emerald-600 dark:text-emerald-400">
                          ₹{grossSalary.toLocaleString("en-IN")}
                        </span>
                      </div>
                      <div className="flex justify-between items-center text-base font-semibold text-gray-900 dark:text-white">
                        <span>Total Deductions</span>
                        <span className="text-xl text-red-600 dark:text-red-500">
                          -₹{(grossSalary - netSalary).toLocaleString("en-IN")}
                        </span>
                      </div>

                      <div className="h-1 bg-indigo-100 rounded-full dark:bg-gray-700 my-3" />

                      <div className="flex justify-between items-center">
                        <span className="text-xl font-bold text-gray-900 dark:text-white">
                          Net Pay (Before Taxes)
                        </span>
                        <span className="text-3xl font-extrabold text-indigo-600 dark:text-indigo-400">
                          ₹{netSalary.toLocaleString("en-IN")}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </TabsContent>

        {showDocumentTab && (
          <TabsContent value="documents">
            <EmployeeDocuments employeeId={empId} />
          </TabsContent>
        )}

        <TabsContent value="leave">
          <EmployeeLeave employeeId={empId} />
        </TabsContent>

        <TabsContent value="Finances">
          <EmployeeLoans employeeId={empId} />
        </TabsContent>

        <TabsContent value="attendance">
          <EmployeeAttendanceNew employeeId={empId} />
        </TabsContent>

        <TabsContent value="payslip">
          <EmployeePayslips employeeId={empId} />
        </TabsContent>
        <TabsContent value="career">
          <CareerHistory employeeId={empId} />
        </TabsContent>
      </Tabs>

      <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {inviteMode === "send"
                ? "Send Portal Invitation?"
                : "Re-Send Invitation?"}
            </DialogTitle>

            <DialogDescription>
              {inviteMode === "send"
                ? "This employee has not received a portal invitation yet. An email will be sent with an activation link."
                : "This employee already has a pending invite. Sending again will renew their access link."}
            </DialogDescription>
          </DialogHeader>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setInviteDialogOpen(false)}
            >
              Cancel
            </Button>

            <Button onClick={handleSendInvite} disabled={inviteLoading}>
              {inviteLoading ? (
                <span className="flex items-center gap-2">
                  Processing…
                  <Loader2 className="h-4 w-4 animate-spin" />
                </span>
              ) : (
                "Confirm"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ViewEmployeeDetails;
